import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack
} from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { useCartState } from 'supplier-portals/uxbeauty/contexts/cart/CartContext';
import { getProductPictureUrl } from 'supplier-portals/uxbeauty/pages/Search/getProductPicture';
import { EventSearchDocument } from 'types/Search';
import { AddToCartForm } from './helpers/AddToCartForm';
import { EventProduct } from 'types/Products';
import { CartItem } from 'supplier-portals/uxbeauty/contexts/cart/types';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import { toast } from 'react-toastify';
import piwik from 'piwik/piwik';
interface AddToCartDialogProps {
  open: boolean;
  onClose: () => void;
  products: EventSearchDocument[] | EventProduct[];
  closeCompare?: () => void;
}

export interface FormState {
  neededBy: Date;
  quantity: number;
  estimatedYearlyUsage: number;
}

export const AddToCartDialog = ({
  open,
  onClose,
  products,
  closeCompare
}: AddToCartDialogProps) => {
  const methods = useForm<FormState>({
    mode: 'onChange'
  });
  const {
    handleSubmit,
    formState: { isValid }
  } = methods;
  const { value, setValue } = useCartState();

  const removeFromCart = (id?: string) => {
    if (id) {
      setValue(prev => ({
        ...prev,
        items: prev.items.filter(item => item.old_key !== id)
      }));
    }
  };

  const send = (data: FormState) => {
    const newItems: CartItem[] = products
      .filter(
        (p): p is (EventSearchDocument | EventProduct) & { old_key: string } =>
          p.old_key !== undefined
      )
      .map(product => ({
        old_key: product.old_key,
        product_id: {
          company: product.company.key ?? '',
          segment: product.segment,
          product: product.key ?? ''
        },
        type: product.type,
        neededBy: data.neededBy,
        estimatedYearlyUsage: data.estimatedYearlyUsage,
        name: product.name,
        quantity: data.quantity,
        imageUrl: getProductPictureUrl(product)
      }));

    setValue(prev => ({ ...prev, items: [...prev.items, ...newItems] }));

    if (newItems.length > 1) {
      piwik.push([
        'trackEvent',
        'UXB cart',
        'Bulk',
        newItems.map(item => item.old_key).toString()
      ]);
    } else {
      piwik.push(['trackEvent', 'UXB cart', 'Single', newItems[0].old_key]);
    }
    toast.success(
      `${newItems.map(item => item.name).join(', ')} added to the cart!`
    );
    onClose();
    closeCompare && closeCompare();
  };

  const productsInCart = products.filter(product =>
    value.items.some(item => item.old_key === product.old_key)
  );

  const disableSubmit = productsInCart.length > 0 || !isValid;

  return (
    <FormProvider {...methods}>
      <Dialog open={open} onClose={onClose} maxWidth="xs">
        <DialogTitle>Request Sample</DialogTitle>
        <form onSubmit={handleSubmit(send)}>
          <DialogContent sx={{ paddingTop: 1 }}>
            {productsInCart.length > 0 && (
              <Stack sx={{ marginBottom: 3 }} gap={1}>
                {productsInCart.map(product => (
                  <Box key={product.old_key}>
                    <Alert
                      icon={false}
                      severity="error"
                      sx={{ width: '100%' }}
                      action={
                        <IconButton
                          onClick={() => removeFromCart(product.old_key)}
                          color="primary"
                        >
                          <DeleteIcon />
                        </IconButton>
                      }
                    >
                      {product.name} is already added to your cart and cannot be
                      added again.
                    </Alert>
                  </Box>
                ))}
              </Stack>
            )}
            <AddToCartForm />
          </DialogContent>
          <DialogActions>
            <Button variant="text" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="contained" type="submit" disabled={disableSubmit}>
              Add to cart
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </FormProvider>
  );
};
